
import React, { createContext, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { Profile, Company, Subscription } from '@/lib/types';
import { useSession } from '@/hooks/useSession';
import { useCompanyState } from '@/hooks/useCompanyState';
import { useAuthActions } from '@/hooks/useAuthActions';
import { useSubscription } from '@/hooks/useSubscription';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  subscription: Subscription | null;
  isLoading: boolean;
  activeCompany: Company | null;
  setActiveCompany: (companyId: string) => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string, requestedCompanyId?: string) => Promise<void>;
  signOut: () => Promise<void>;
  hasPendingCompanyRequest: boolean;
  pendingCompany: { id: string; name: string } | null;
  setProfile: (profile: Profile | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { session, user, profile, isLoading, setProfile } = useSession();
  const { subscription } = useSubscription(user?.id);
  const { 
    activeCompany, 
    hasPendingCompanyRequest, 
    pendingCompany, 
    setActiveCompany 
  } = useCompanyState(user?.id);
  const { signIn, signUp, signOut } = useAuthActions();

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        subscription,
        isLoading,
        activeCompany,
        setActiveCompany,
        signIn,
        signUp,
        signOut,
        hasPendingCompanyRequest,
        pendingCompany,
        setProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
